import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { mockCases } from '@/mocks/cases';
import { mockChildren } from '@/mocks/children';
import { useAuth } from '@/context/AuthContext';

export default function MyCasesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const myCases = mockCases.filter((c) => c.assignedWorkerId === 'u1' || c.assignedWorker === user?.name);
  const filtered = myCases.filter((c) => {
    const matchFilter = filter === 'all' || c.status === filter || c.riskLevel === filter;
    const matchSearch = c.childName.toLowerCase().includes(search.toLowerCase()) || c.caseNumber.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const riskColor = (r: string) => r === 'critical' ? 'bg-red-100 text-red-700 border-red-200' : r === 'high' ? 'bg-orange-100 text-orange-700 border-orange-200' : r === 'moderate' ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-green-100 text-green-700 border-green-200';
  const statusColor = (s: string) => s === 'active' ? 'bg-teal-100 text-teal-700' : s === 'pending_review' ? 'bg-amber-100 text-amber-700' : s === 'referred' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600';

  return (
    <AppLayout title="My Cases" breadcrumb={['Home', 'My Cases']}>
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Assigned', value: myCases.length, color: 'text-gray-800', bg: 'bg-white' },
          { label: 'Active', value: myCases.filter((c) => c.status === 'active').length, color: 'text-teal-600', bg: 'bg-teal-50' },
          { label: 'Pending Review', value: myCases.filter((c) => c.status === 'pending_review').length, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Critical', value: myCases.filter((c) => c.riskLevel === 'critical').length, color: 'text-red-600', bg: 'bg-red-50' },
        ].map((s) => (
          <div key={s.label} className={`${s.bg} rounded-xl border border-gray-100 p-4`}>
            <p className={`text-3xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-sm text-gray-500 mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200 flex-1 min-w-48">
          <i className="ri-search-line text-gray-400 text-sm" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search cases..." className="bg-transparent text-sm outline-none w-full text-gray-700 placeholder-gray-400" />
        </div>
        {['all', 'active', 'pending_review', 'critical', 'high', 'closed'].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`text-sm px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap capitalize ${filter === f ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {f.replace('_', ' ')}
          </button>
        ))}
      </div>

      {/* Cases Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((c) => {
          const child = mockChildren.find((ch) => ch.id === c.childId);
          return (
            <div key={c.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-teal-200 transition-all cursor-pointer" onClick={() => navigate(`/child-profiles/${c.childId}`)}>
              <div className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  {child && <img src={child.photo} alt={c.childName} className="w-12 h-12 rounded-xl object-cover object-top flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{c.childName}</p>
                    <p className="text-xs text-gray-500">{c.age} yrs · {c.gender} · {c.incidentType}</p>
                    <p className="text-xs font-mono text-gray-400 mt-0.5">{c.caseNumber}</p>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full border font-medium capitalize flex-shrink-0 ${riskColor(c.riskLevel)}`}>{c.riskLevel}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${statusColor(c.status)}`}>{c.status.replace('_', ' ')}</span>
                  <span className="text-xs text-gray-400">Updated {c.updatedAt}</span>
                </div>
              </div>
              <div className={`h-1 w-full ${c.riskLevel === 'critical' ? 'bg-red-400' : c.riskLevel === 'high' ? 'bg-orange-400' : c.riskLevel === 'moderate' ? 'bg-amber-400' : 'bg-green-400'}`} />
            </div>
          );
        })}
      </div>
      
      {filtered.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-16 text-center">
          <i className="ri-folder-open-line text-4xl text-gray-300 mb-3 block" />
          <p className="text-gray-500">No cases found</p>
        </div>
      )}
    </AppLayout>
  );
}
